import { hasViewerSetupState, isViewerSetupReady, } from '#pplx/frontend/apps/ask/sources/utilities/sourceSetupState.js';
const PIPEDREAM_SOURCE_ID_SUFFIX = '__pipedream';
const EXPLICIT_CONNECTION_REQUIRED_SOURCE_IDS = new Set([
    'plaid',
    'health',
    'medical_records',
    'wearables',
    'function_health',
    'apple_healthkit',
]);
export function normalizePipedreamSourceId(sourceId) {
    if (!sourceId.endsWith(PIPEDREAM_SOURCE_ID_SUFFIX)) {
        return sourceId;
    }
    const appSlug = sourceId.slice(0, -PIPEDREAM_SOURCE_ID_SUFFIX.length);
    return `${appSlug.trim().toLowerCase().replaceAll('-', '_')}${PIPEDREAM_SOURCE_ID_SUFFIX}`;
}
/**
 * Canonical, fail-closed read of a source's connector-RBAC `access` tri-state
 * (ConnectorUseState), independent of the legacy `org_disabled` flag. Returns
 * `'none'` when `access` is absent (org not in enforcement mode). Any present
 * value other than the known `'usable'` / `'denied_by_policy'` — including an
 * unrecognized *future* enum value — fails closed to `'disabled_by_org'`, so a
 * server enum that grows never silently weakens a gate that switches on this.
 */
export function getSourceAccessVerdict(source) {
    const access = source.access;
    if (access == null) {
        return 'none';
    }
    if (access === 'usable') {
        return 'usable';
    }
    if (access === 'denied_by_policy') {
        return 'denied_by_policy';
    }
    return 'disabled_by_org';
}
/**
 * Connector-RBAC / org authorization for a source, independent of query-picker
 * selectability. `access` is authoritative when present (only `usable` is
 * permitted); otherwise fall back to the legacy `org_disabled` boolean. Excludes
 * the `is_user_selectable` gate so auto-managed connectors (health, local MCP)
 * still read as usable/connected in connector settings.
 */
export function isSourceAccessAllowed(source) {
    const verdict = getSourceAccessVerdict(source);
    return verdict === 'usable' || (verdict === 'none' && source.org_disabled !== true);
}
/**
 * True when Segment 6-1's connector-RBAC `access` tri-state marks a source as
 * unavailable — any present value other than `usable`. Derives from the shared
 * fail-closed `getSourceAccessVerdict`, so the known `disabled_by_org` /
 * `denied_by_policy` states and any unrecognized *future* enum value alike block,
 * and a server enum that grows never silently weakens the top guard in
 * `isSourceConnectedIndividually`. Deliberately independent of the legacy
 * `org_disabled` flag: returns false when `access` is absent (legacy /
 * shadow-enforcement modes), where the verdict is `'none'`.
 */
export function isSourceAccessBlocked(source) {
    const verdict = getSourceAccessVerdict(source);
    return verdict !== 'none' && verdict !== 'usable';
}
/**
 * Classifies why a source is unavailable, most-severe first: `disabled_by_org`
 * (the connector is off org-wide) outranks `denied_by_policy` (role lacks
 * access). Derives from the shared fail-closed `getSourceAccessVerdict`, so an
 * unrecognized *future* `access` value classifies as `disabled_by_org` rather
 * than leaking through as available, and a stale legacy `org_disabled === true`
 * still wins over a role denial. When `access` is absent (legacy /
 * shadow-enforcement) only the legacy `org_disabled` boolean can classify.
 * Mirrors `isSourceAccessAllowed`: `getSourceUnavailableReason(s) === null` iff
 * `isSourceAccessAllowed(s)`.
 */
export function getSourceUnavailableReason(source) {
    const verdict = getSourceAccessVerdict(source);
    if (verdict === 'disabled_by_org' || (verdict !== 'usable' && source.org_disabled === true)) {
        return 'disabled_by_org';
    }
    if (verdict === 'denied_by_policy') {
        return 'denied_by_policy';
    }
    return null;
}
/**
 * Execution environments that make a source usable for Ask queries. A source
 * that supports neither is reachable only through Computer (CLI).
 */
export const ASK_EXEC_ENVS = ['mcp', 'native_api'];
/**
 * True when a source only runs as a CLI, so connecting it does nothing for Ask
 * queries and it is usable only through Computer. Absent or empty
 * `supported_exec_envs` means the backend hasn't classified the source, which
 * must not be read as Computer-only.
 *
 * Mirrors the backend's `is_source_ask_compatible`, minus its
 * `FILE_CONNECTOR_SOURCE_IDS` carve-out (file connectors count as Ask-usable
 * regardless of exec envs). No file connector is CLI-only today, so the two
 * only diverge hypothetically — this is a label, never a gate.
 */
export function isComputerOnlySource(source) {
    const execEnvs = source.supported_exec_envs;
    if (execEnvs == null || execEnvs.length === 0) {
        return false;
    }
    return !execEnvs.some((execEnv) => ASK_EXEC_ENVS.includes(execEnv));
}
/**
 * Checks whether a source is allowed for the current user.
 *
 * A source is allowed when it is user-selectable in the query picker and its
 * connector-RBAC / org state permits use (`getSourceUnavailableReason` is null).
 */
export function isSourceAllowed(source) {
    return source.is_user_selectable !== false && getSourceUnavailableReason(source) === null;
}
/**
 * Checks whether a source is connected and ready to use.
 *
 * - Plaid and health connector surfaces require an explicit user connection.
 * - Scoped-config sources use viewer_setup_state.next_action as the source of truth.
 * - Sources with no auth requirement are connected if allowed.
 * - System-auth sources are connected if allowed.
 * - Connector-type sources (`other` / `remote_mcp`) require at least one user connection.
 * - All other sources are connected if allowed.
 * - Grouped source-family primaries are connected if any member is individually connected.
 *
 * Member aggregation is connector-RBAC-safe: each member is guarded by its own
 * access check inside `isSourceConnectedIndividually`, so an access-blocked
 * member (`disabled_by_org` / `denied_by_policy`) never counts toward the
 * family. Segment 6b annotates `access` per source id, so a family can be
 * mixed — e.g. a denied primary alongside a usable, connected sibling. The
 * "connected if any usable variant is connected" semantic is intentional so a
 * legitimately usable sibling is never hidden; how a collapsed mixed-family
 * row should present that mix is an open product question (see PR #179436).
 */
export function isSourceConnected(source) {
    if (isSourceConnectedIndividually(source)) {
        return true;
    }
    return source.family_info?.members?.some(isSourceConnectedIndividually) === true;
}
export function isSourceIdConnectedByMigration(sourceId, sources) {
    const normalizedSourceId = normalizePipedreamSourceId(sourceId);
    return sources.some((source) => {
        const migration = getSourceMigration(source);
        return (isSourceAllowed(source) &&
            migration?.target_connector_status === 'CONNECTED' &&
            migration.legacy_source_ids.some((legacySourceId) => normalizePipedreamSourceId(legacySourceId) === normalizedSourceId));
    });
}
function requiresExplicitConnection(source) {
    return EXPLICIT_CONNECTION_REQUIRED_SOURCE_IDS.has(source.id);
}
function hasUserConnection(source) {
    return 'user_connections' in source && Boolean(source.user_connections?.length);
}
function getSourceMigration(source) {
    return 'migration' in source ? source.migration : undefined;
}
/**
 * Returns true for connector-type sources that report as connected without any
 * explicit user-level auth — i.e. no-auth or system-auth connectors that the
 * user has not personally OAuthed. These get surfaced as "connected" everywhere
 * by default; callers that want to deprioritize them (e.g. the dropdown's
 * default view) should filter on this.
 */
export function isPassivelyConnectedConnector(source) {
    if (source.type !== 'other' && source.type !== 'remote_mcp') {
        return false;
    }
    if (requiresExplicitConnection(source) || hasUserConnection(source)) {
        return false;
    }
    return source.auth_type === 'none' || source.auth_scope === 'system';
}
/** Checks the source's own connection state without aggregating source-family members. */
export function isSourceConnectedIndividually(source) {
    // Truthful states (Part 5 / Segment 7b): a source the connector-RBAC access
    // tri-state marks unavailable is never connected, regardless of any existing
    // user connection. This routes every connected-status surface (connectors
    // list, family cards, @-mention, plus-menu suggestions, source picker
    // grouping) through the same denial. Inert when `access` is absent, so
    // legacy / shadow-mode sources behave exactly as before.
    if (isSourceAccessBlocked(source)) {
        return false;
    }
    if (requiresExplicitConnection(source)) {
        // Gate on access/org authorization (not `isSourceAllowed`): a role-denied
        // or org-disabled high-sensitivity connector must not stay selectable via a
        // stale user_connection, but these connectors are intentionally
        // non-selectable in the query picker, so the `is_user_selectable` gate in
        // `isSourceAllowed` would wrongly report a legitimately connected one as
        // disconnected.
        return isSourceAccessAllowed(source) && hasUserConnection(source);
    }
    if (hasViewerSetupState(source)) {
        return isSourceAllowed(source) && isViewerSetupReady(source);
    }
    if (source.auth_type === 'none') {
        return isSourceAllowed(source);
    }
    if (source.auth_scope === 'system') {
        return isSourceAllowed(source);
    }
    if (source.type === 'other' || source.type === 'remote_mcp') {
        // Access-gate the highest-traffic connector type (regular OAuth: gmail,
        // notion, slack…) at the predicate itself — a role-denied source with a
        // stale user_connection must never read as connected. This closes the leak
        // for ALL callers (sources/at-mention suggestion menus reach this predicate
        // directly, bypassing useIsProviderConnected). Uses the access-only helper
        // so non-selectable auto-managed connectors aren't spuriously disconnected.
        return isSourceAccessAllowed(source) && Boolean(source.user_connections?.length);
    }
    return isSourceAllowed(source);
}
//# sourceMappingURL=sourceStatusPredicates.js.map